import { PDFDocumentRect } from "@/components/PDFViewer/PDFViewerHooks";
import { ObjectId } from "@/model/db/General";
import { Contact } from "../../../../../model/db/Contact";
import BaseAsset, {
  TypedBaseAsset,
} from "../../../../../model/general-assets/BaseAsset";
import { DocumentStoreDocument } from "../../../../../modules/document-store/DSInterfaces";
import { LinkedAsset } from "../../model/CashBudgetEntry";

export type RentalUnitTypeGroup =
  | "commercial"
  | "residental"
  | "parking"
  | "storage"
  | "other";
export const allRentalUnitTypeGroups: RentalUnitTypeGroup[] = [
  "commercial",
  "residental",
  "parking",
  "storage",
  "other",
];

export type RentalStatus = "occupied" | "vacant" | "reserved" | "blocked";
export const allRentalStatus: RentalStatus[] = [
  "occupied",
  "vacant",
  "reserved",
  "blocked",
];
type RentalUnitData = {
  id: string;
  displayName: string;
  entity: string;
  objectId: string;
  type: string;
  unitType: string; // id RentalUnitType
  floor?: number;
  building?: string;
  note?: string;
  // if unittype group is parking or other
  quantity: number;

  // if unittype group is commercial or residental
  area: number;

  lastPlandataChangeDate?: Date;
  lastPlandataChangeUser?: string;

  rentGross: number;
  rentNet: number;
  operatingCostGross: number;
  operatingCostNet: number;

  rentalStatus: RentalStatus;
  status: "active" | "archived";
  administratedTo: Date;
  administratedFrom?: Date;
  attachments: DocumentStoreDocument[];
};
export interface RentalUnit extends BaseAsset {
  data: RentalUnitData;
}

export interface EnrichtedRentalUnit extends RentalUnit {
  // extends the data of the rental unit with additional data
  data: RentalUnitData & {
    agreement?: RentalAgreement;
    tenant?: Contact;
    vacant?: RentalVacancy;
  };
}

export interface EnrichtedRentalUnitKPISGathered extends EnrichtedRentalUnit {
  kpis: {
    rentPerPlan: number;
    rentPerCurrent: number;
    rentPerAreaPlan: number;
    rentPerAreaCurrent: number;
    rentPerAreaDeficit: number;
    rentPerQuantityPlan: number;
    rentPerQuantityCurrent: number;
    rentPerQuantityDeficit: number;
    rentNetCurrent: number;
    rentGrossCurrent: number;
    rentNetDeficitCurrent: number;
    rentGrossDeficitCurrent: number;
    operatingCostNetCurrent: number;
    operatingCostGrossCurrent: number;
    operatingCostNetDeficitCurrent: number;
    operatingCostGrossDeficitCurrent: number;
  };
}

export const convertEnrichtedRentalUnitToEnrichtedRentalUnitKPISGathered = (
  data: EnrichtedRentalUnit[]
) => {
  return data.map((rentalUnit) => {
    const rentNetCurrent = rentalUnit.data.agreement?.data.rentNet || 0;
    const rentGrossCurrent = rentalUnit.data.agreement?.data.rentGross || 0;
    const rentNetDeficitCurrent = rentNetCurrent - rentalUnit.data.rentNet;
    const rentGrossDeficitCurrent =
      rentGrossCurrent - rentalUnit.data.rentGross;
    const operatingCostNetCurrent =
      rentalUnit.data.agreement?.data.operatingCostNet || 0;
    const operatingCostGrossCurrent =
      rentalUnit.data.agreement?.data.operatingCostGross || 0;
    const operatingCostNetDeficitCurrent =
      operatingCostNetCurrent - rentalUnit.data.operatingCostNet;
    const operatingCostGrossDeficitCurrent =
      operatingCostGrossCurrent - rentalUnit.data.operatingCostGross;

    const rentPerAreaPlan =
      rentalUnit.data.area > 0
        ? rentalUnit.data.rentNet / rentalUnit.data.area
        : 0;
    const rentPerAreaCurrent =
      rentalUnit.data.area > 0 ? rentNetCurrent / rentalUnit.data.area : 0;
    const rentPerAreaDeficit =
      rentalUnit.data.area > 0 ? rentPerAreaPlan - rentPerAreaCurrent : 0;
    const rentPerQuantityPlan =
      rentalUnit.data.quantity > 0
        ? rentalUnit.data.rentNet / rentalUnit.data.quantity
        : 0;
    const rentPerQuantityCurrent =
      rentalUnit.data.quantity > 0
        ? rentNetCurrent / rentalUnit.data.quantity
        : 0;
    const rentPerQuantityDeficit =
      rentalUnit.data.quantity > 0
        ? rentPerQuantityPlan - rentPerQuantityCurrent
        : 0;
    return {
      ...rentalUnit,
      kpis: {
        rentPerPlan: rentPerAreaPlan || rentPerQuantityPlan,
        rentPerCurrent: rentPerAreaCurrent || rentPerQuantityCurrent,
        rentPerAreaPlan,
        rentPerAreaCurrent,
        rentPerAreaDeficit,
        rentPerQuantityPlan,
        rentPerQuantityCurrent,
        rentPerQuantityDeficit,
        rentNetCurrent,
        rentGrossCurrent,
        rentNetDeficitCurrent,
        rentGrossDeficitCurrent,
        operatingCostNetCurrent,
        operatingCostGrossCurrent,
        operatingCostNetDeficitCurrent,
        operatingCostGrossDeficitCurrent,
      },
    };
  });
};

export type RentalAgreementUnit = {
  rentalUnitId: string;
  from: Date;
  to?: Date;
};

export interface RentalAgreementTimePeriod extends BaseAsset {
  data: {
    reason?: string;
    rentPaymentTimingOptions: RentPaymentTimingOption;
    note: string;

    type: string;
    objectId: string;
    entity: string;
    rentalAgreement: string;
    isPlanned: boolean;

    taxable: boolean;
    isVacantPeriod: boolean;
    rentalUnitIds: string[];
    from: Date;
    to?: Date;
    rentGross: number;
    rentNet: number;
    operatingCostGross: number;
    operatingCostNet: number;
    paymentPositions: PaymentPosition[];
  };
}

export type PaymentPosition = {
  id: string;
  net: number;
  gross: number;
};

export type DepositPosition = {
  id: string;
  depositValue: number;
  depositStart: Date;
  comment?: string;
};

export type RentPaymentTimingOption =
  | "startOfMonth"
  | "endOfMonth"
  | "middleOfMonth";
export interface RentalAgreement extends BaseAsset {
  nested: {
    tenantName?: string;
    entityNumber: string;
    entityName: string;
    objectName: string;
    objectNumber: string;
    // unitUnitType: string[];
    // unitQuantity: number;
    // unitArea: number;
  };
  expand: {
    rentalUnits?: RentalUnit[];
    tenant?: Contact;
  };
  data: {
    type: string;
    entity: string;
    objectId: string;
    rentalUnits: string[];
    contractType: "business" | "private";
    rentPaymentTimingOptions: RentPaymentTimingOption;

    balance?: number;
    currentState?: string;
    id: string;
    displayName: string;

    tenant: string; // id Tenant

    moveIn: Date;
    agreementExpiration: Date | null;
    moveOut: Date | null;
    stopDebitRun?: boolean;
    // from: Date;
    // to?: Date;
    status: "active" | "archived";
    attachments: DocumentStoreDocument[];
    note?: string;

    // deposit?: number;
    taxable?: boolean;

    //todo: add custom fields
    customFields?: {
      [key: string]: any; // todo: define custom fields
    };

    rentGross: number;
    rentNet: number;
    operatingCostGross: number;
    operatingCostNet: number;
    rentGrossTotal: number;
    rentNetTotal: number;
    paymentPositions: PaymentPosition[];
    lastRentChangeDate: Date;

    dunning?: {
      stopDunningRun?: boolean;
      currentDunningActivtiy?: string;
    };
    calculated: {
      rentWithOperatingCostsGross: number;
      rentWithOperatingCostsNet: number;
    };

    copayment?: RentalAgreementCopayment;
    deposit: DepositPosition[];
    automatic?: RentalAgreementAutomatic;
    contactOption?: ContactOption;
    graduatedRent?: GraduatedRent[];
    index?: IndexOption;
    option?: AgreementOption[];
    marks?: RentalAgreementMarks;
    keyHandover?: {
      date: Date;
      info?: string;
      status: "open" | "closed";
    }
    // mietart staffelmiete, indexmiete, anpassung, andere
    // optionsrechte -
    // verwaltergebühr
    //
    //
    //
    //
  };
}
export type RentalAgreementMarks = {
  tenant?: PDFDocumentRect[];
  rentalUnits?: PDFDocumentRect[];
  copayment?: PDFDocumentRect[];
  paymentInfo?: PDFDocumentRect[];
  deposit?: PDFDocumentRect[];
  rentDuration?: PDFDocumentRect[];
  option?: PDFDocumentRect[];
  automatic?: PDFDocumentRect[];
  index?: PDFDocumentRect[];
  graduatedRent?: PDFDocumentRect[];
};

export type GraduatedRent = {
  fromDate: Date;
  paymentPositions: PaymentPosition[];
};

export type RentalAgreementAutomatic = {
  monthBeforeContractEnd: number;
  extendByMonths: number;
};
export type RentalAgreementCopayment = {
  maxByYear?: number;
  valueByCase?: number;
  note?: string;
};
export type ContactOption = {
  addressId: string;
  contactPersonId: string | null;
  mail: string[] | null;
  phone: string[] | null;
  sms: string[] | null;
};
export type AgreementOption = {
  optionUntil: Date;
  optionBy: "tenant" | "landlord" | "automatic";
  extendsByMonths: number;
  optionUsed: boolean;
  note: string;
};

export type IndexOption = {
  type: "default" | "yearly";
  lastPriceIndex?: number;
  lastPriceIndexChange?: Date;
  ignoreUntil?: Date;
  rentChangeFactor: number;
  paragraph: string;
  agreementLabel: string;
  backPayPossible: boolean;
  note: string;
} & (
  | {
      type: "default";
      indexFactor: number;
    }
  | {
      type: "yearly";
      indexMonth:
        | "1"
        | "2"
        | "3"
        | "4"
        | "5"
        | "6"
        | "7"
        | "8"
        | "9"
        | "10"
        | "11"
        | "12";
    }
);

type RentalAgreementPlanned = RentalAgreementPlannedRent;

interface RentalAgreementPlannedRent {
  type: "RENT";
  user: string | "system";

  from: Date;
  to: Date | null;

  rentGross: number;
  rentNet: number;

  operatingCostGross: number;
  operatingCostNet: number;
}

export interface RentalVacancy extends BaseAsset {
  nested: {
    entity: string;
    objectName: string;
    objectNumber: number;
    unitUnitType: string;
    unitQuantity: number;
    unitArea: number;
  };
  expand?: {
    rentalUnit: RentalUnit;
  };
  data: {
    id?: string;

    type: string;
    entity: string;
    objectId: string;
    rentalUnit: string;

    displayName: string;
    moveIn: Date;
    moveOut: Date | null;
    currentState?: string;
    note?: string;

    rentGross: number;
    rentNet: number;

    advertisements: RentalVacancyAdvertisement[];
    tags?: string[];
  };
}

export interface RentalVacancyAdvertisement {
  id: string;
  link: string;
  platform: string;
  from?: Date;
  to?: Date;
  note?: string;
  status: "active" | "closed";
}

export interface SubmitObjectPlanData {
  rentalUnitId: string;
  rentGross: number;
  rentNet: number;
  operatingCostGross: number;
  operatingCostNet: number;
}

export type RentalUnitFormValue = {
  id: string;
  displayName: string;
  unitType: string;
  unitGroup: string;
  quantity: number;
  area: number;
  building: string;
  floor: number;
  rentGross: number;
  rentNet: number;
  operatingCostGross: number;
  operatingCostNet: number;
  administratedFrom: Date;
  administratedTo: Date;
};

export type RentalAdjustmentFormData = {
  adjustmentType: "reduction" | "loss";
  rentalAgreementId: ObjectId;
  from: Date;
  to: Date;
  rentNet: number;
  rentGross: number;
  operatingCostsNet: number;
  operatingCostsGross: number;
  linkedAsset: LinkedAsset[];
  note: string;
  status: "active" | "archived";
};

export type RentalAdjustmentData = RentalAdjustmentFormData & {
  objectId: string;
  entity: string;
  type: string;
};
export type RentalAdjustment = TypedBaseAsset<RentalAdjustmentData>;
